import React, { createRef } from 'react';
import { cssTheme } from '@uds/theme-utils';
import { useStyles } from '@team-griffin/stylr';
import { dispatchGAEvent } from '@uds/tagmg';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { isEmpty } from 'presentation/utils/generalUtils';
import { createRibbonName } from 'domain/transformers/ribbons';
import { SearchInput } from '@uds/input';
import Button from '@uds/button';
import { Glyph, IconName } from '@uds/glyphs';

import { createFqdn, prefxTld } from './utils';
import { checkValidity } from '../domain-search-input/components/utils';

interface SearchProps {
  tld: string;
  glyph: IconName;
  label: string;
  placeholder?: string;
  destination?: string;
}
export interface OuterProps {
  search: SearchProps;
  isStrip?: boolean;
  id: string;
  ribbonName?: string;
}

const useStylesheet = ({ isStrip }: { isStrip: boolean }) => {
  return useStyles(
    {
      withMargin: {
        search: {
          marginTop: cssTheme.gu3,
        },
      },
    },
    { withMargin: !isStrip },
  );
};

interface ExtendedEventTarget extends EventTarget {
  domain: {
    value: string;
  };
}

const useOnSubmit = ({
  search,
  formName,
  invalidMessage,
}: {
  search: SearchProps;
  formName: string;
  invalidMessage: string;
}) => {
  const history = useHistory();
  return (e: any) => {
    e.preventDefault();
    const target = e.target as ExtendedEventTarget;

    const domain = target.domain.value.trim();
    if (domain.length > 2) {
      const tld = prefxTld(search.tld);
      const fqdn = createFqdn(domain, tld);
      const target = `${search.destination}?domain=${encodeURIComponent(fqdn)}`;

      dispatchGAEvent({
        hitType: 'event',
        eventCategory: 'USF Interaction',
        eventAction: 'form submit',
        eventLabel: createRibbonName(formName),
      });
      history.push(target);
    } else {
      e.target.domain.setCustomValidity(invalidMessage);
      return false;
    }
  };
};
const DomainSearchForm = ({ search, isStrip = false, id, ribbonName = 'domainSearchStart' }: OuterProps) => {
  const { glyph, label, placeholder } = search;
  const { styles } = useStylesheet({ isStrip });

  const formName = `usf.${ribbonName}.${isEmpty(id) ? 'domainSearchStart' : id}`;
  const intl = useIntl();
  const invalidMessage = intl.formatMessage({
    id: 'dst.v2.domain.search.input.errors.short',
  });
  const onSubmit = useOnSubmit({
    search,
    formName,
    invalidMessage,
  });
  const inputRef = createRef<HTMLInputElement>();

  return (
    <div style={styles.search}>
      <form method="POST" onSubmit={onSubmit} data-selector="domain-search-form">
        <SearchInput
          suffix={search.tld}
          inputProps={{
            placeholder,
            name: 'domain',
            required: 'required',
            type: 'text',
            ref: inputRef,
            onChange: (e: any) => {
              e.target.setCustomValidity('');
              e.target.reportValidity();
            },
          }}
          action={
            <Button
              type="submit"
              onClick={() => {
                if (inputRef && inputRef.current) {
                  checkValidity(inputRef.current, invalidMessage);
                }
              }}
              glyph={<Glyph name={glyph} />}
            >
              {label}
            </Button>
          }
        />
      </form>
    </div>
  );
};

DomainSearchForm.displayName = 'SearchForm';

export default DomainSearchForm;
